import { type Page, toListQuery } from '@hello-aria/makalu-api-client'
import type {
    Debtor,
    CreateDebtor,
    DeleteDebtorContact,
    CreateDebtorGateway,
    UpsertDebtor,
    UpsertDebtorGateway,
    Contact
} from '@/shared/models/debtorModels'
import { requireDebtorCountry } from '@/shared/models/debtorModels'
import type { Company } from '@/shared/models/companyModel'
import { makaluApi } from '@/shared/utils/makaluApiClient'

import { isNil, isEmpty } from 'lodash'
import type { QueryStringInput } from '@hello-aria/query-string'
import { removeEmailWhitespace } from '@/shared/utils/inputUtils'
import type { DocuSignContract } from '@/shared/models/contractModels'
import { trackEvent } from '@/shared/utils/analyticsUtils'
import { sanitizeAddress } from '@/shared/models/addressModels'
import { handleHttpError } from '@/shared/utils/queryUtils'

export const createDebtor = async (payload: CreateDebtor, companyId: Company['id']): Promise<Debtor | undefined> => {
    const { first, city, postal } = payload.address ?? {}
    const country = requireDebtorCountry(payload.identifier.country)

    const formattedContacts = (() => {
        if (!isNil(payload.contacts) && !isEmpty(payload.contacts)) {
            return payload.contacts.map((contact) => removeEmailWhitespace(contact))
        }
        if (payload.contact) {
            return [removeEmailWhitespace(payload.contact)]
        }
        return []
    })()

    const formattedPayload: CreateDebtorGateway = {
        country,
        name: payload.name,
        identifier: payload.identifier,
        contacts: formattedContacts,
        address:
            first && city && postal ? { first, city, postal, country: payload.address?.country || country } : undefined
    }

    const response = await makaluApi.POST('/companies/{companyId}/debtors', {
        params: { path: { companyId } },
        body: formattedPayload
    })

    trackEvent('Create_Debtor')

    return response
}

interface GetDebtors {
    companyId: string
    query?: QueryStringInput
}

export const listDebtors = async (params: GetDebtors): Promise<Page<Debtor> | undefined> => {
    try {
        const response = await makaluApi.listPage('/companies/{companyId}/debtors', {
            params: { path: { companyId: params.companyId }, query: toListQuery(params.query ?? {}) }
        })
        return response
    } catch (error) {
        return handleHttpError('listDebtors', error)
    }
}

export const getDebtor = async (
    debtorId: Debtor['id'] | undefined | null,
    companyId: Company['id']
): Promise<Debtor | undefined> => {
    try {
        if (isNil(debtorId)) {
            return
        }
        const response = await makaluApi.GET('/companies/{companyId}/debtors/{debtorId}', {
            params: { path: { companyId, debtorId } }
        })
        return response
    } catch (error) {
        return handleHttpError('getDebtor', error)
    }
}

export const deleteDebtorContact = async (
    companyId: Company['id'],
    payload: DeleteDebtorContact
): Promise<Debtor | undefined> => {
    try {
        // We use the create endpoint (upsert) to update the contacts list of the given debtor
        const formattedPayload: CreateDebtorGateway = {
            country: payload.country,
            identifier: payload.identifier,
            name: payload.name,
            contacts: payload.contacts.filter((contact) => contact.email !== payload.emailToDelete)
        }

        const response = await makaluApi.POST('/companies/{companyId}/debtors', {
            params: { path: { companyId } },
            body: formattedPayload
        })

        trackEvent('Update_Debtor_Contact')

        return response
    } catch (error) {
        return handleHttpError('deleteDebtorContact', error)
    }
}

function mergeContactsByEmail(baseContacts: Contact[], newContacts: Contact[] = []): Contact[] {
    const merged = new Map([...baseContacts, ...newContacts].map((c) => [c.email, c]))
    return Array.from(merged.values())
}

export const upsertDebtor = async (companyId: Company['id'], payload: UpsertDebtor): Promise<Debtor | undefined> => {
    // We use the create endpoint (upsert)
    const { identifier } = payload
    if (isNil(identifier)) throw new Error('A debtor upsert needs the identifier of the debtor')

    const contacts: Contact[] = mergeContactsByEmail(payload.existingContacts, payload.contacts)

    const formattedPayload: UpsertDebtorGateway = {
        name: payload.name ?? null,
        country: requireDebtorCountry(identifier.country),
        identifier,
        contacts,
        address: sanitizeAddress(payload.address)
    }

    const response = await makaluApi.POST('/companies/{companyId}/debtors', {
        params: { path: { companyId } },
        body: formattedPayload
    })

    trackEvent('Update_Debtor')

    return response
}

type GetDebtorContractService = {
    companyId: string | undefined
    debtorId: string | null | undefined
}

export const getDebtorContract = async ({
    companyId,
    debtorId
}: GetDebtorContractService): Promise<DocuSignContract | undefined> => {
    try {
        if (!companyId || !debtorId) {
            throw new Error('getDebtorContract: companyId or debtorId is missing')
        }

        const response = await makaluApi.GET('/private/companies/{companyId}/guarantor-contracts/{debtorId}/envelope', {
            params: { path: { companyId, debtorId } }
        })
        return response
    } catch (error) {
        return handleHttpError('getDebtorContract', error)
    }
}
